import { useCurrentWallet, useWalletProperties } from '@plug-core/state'
import { Stack, Typography, useTheme } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlinePlusCircle, HiDocumentText, HiKey } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import WalletDetailsPrompt from './WalletDetailsPrompt'
import { logger } from '@plug-core/logger'
import useFindTokens from '@plug-core/hooks/useFindTokens'
import { ROUTES } from '@utils/routes'
import { addNewAccount } from '@plug-core/handlers/walletActions/wallet'

const WalletImportTypePicker = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [promptVisible, setPromptVisible] = useState(false)
  const [name, setName] = useState<string>('')
  const [emoji, setEmoji] = useState<string>('🚀')

  const { currentWallet, setCurrentWallet } = useCurrentWallet()
  const { setWalletEmoji, setWalletName } = useWalletProperties()
  const { findTokens } = useFindTokens()

  const importMethods = useMemo(
    () => [
      {
        type: 'new',
        icon: HiOutlinePlusCircle,
        color: theme.palette.rainbow.red,
        onClick: () => setPromptVisible(true),
      },
      {
        type: 'privateKey',
        icon: HiDocumentText,
        color: theme.palette.rainbow.green,
        onClick: () => navigate(ROUTES.PEM_IMPORT),
      },
      {
        type: 'stoic',
        icon: HiKey,
        color: theme.palette.rainbow.yellow,
        onClick: () => navigate(ROUTES.STOIC_IMPORT),
      },
    ],
    []
  )

  const createNewWallet = useCallback(async () => {
    const newAccount = await addNewAccount(currentWallet)
    await findTokens(newAccount.toString())
    setCurrentWallet(newAccount.toString())
    setWalletName(newAccount.toString(), name || '')
    setWalletEmoji(newAccount.toString(), emoji)
    setPromptVisible(false)
    navigate(-1)
  }, [name, setWalletName, emoji])

  return (
    <Stack>
      <Stack
        bgcolor={(theme) => theme.palette.background.paper}
        mx={2}
        borderRadius={2}
        spacing={1}
      >
        {importMethods.map(({ icon: MethodIcon, type, color, onClick }) => (
          <Stack p={2} key={type} onClick={onClick}>
            <MethodIcon color={color} size={30} style={{ marginLeft: -3 }} />
            <Typography variant="h6">{t(`importWallet.${type}.title`)}</Typography>
            <Typography variant="caption">{t(`importWallet.${type}.description`)}</Typography>
          </Stack>
        ))}
      </Stack>
      <WalletDetailsPrompt
        show={promptVisible}
        name={name}
        emoji={emoji}
        setName={setName}
        setEmoji={setEmoji}
        onSave={() =>
          createNewWallet().catch((e) =>
            logger.error('Failed to create new wallet', { originalError: e })
          )
        }
        onClose={() => setPromptVisible(false)}
      />
    </Stack>
  )
}

export default WalletImportTypePicker
